import { ScrollingModule } from "@angular/cdk/scrolling";
import {
  Component,
  forwardRef,
  input,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
  signal,
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ImageUrlDirective } from "@app/directives";
import { Observable, Subscription } from "rxjs";

export interface FormAutocompleteOption<T = any> {
  value: T;
  label: string;
  displayLabel?: string; // Optional: different label to show after selection
  count?: number;
  disabled?: boolean;
  imageSrc?: string; // Optional: image URL to display in the dropdown
}

@Component({
  selector: "fh-form-autocomplete",
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    ScrollingModule,
    MatProgressSpinnerModule,
    ImageUrlDirective,
  ],
  templateUrl: "./form-autocomplete.component.html",
  styleUrls: ["./form-autocomplete.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormAutocompleteComponent),
      multi: true,
    },
  ],
})
export class FormAutocompleteComponent<T = any>
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  // Inputs
  options = input.required<FormAutocompleteOption<T>[]>();
  label = input<string>("");
  placeholder = input<string>("Select...");
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  showCounts = input<boolean>(false);
  appearance = input<"fill" | "outline">("fill");
  // API search function - if provided, will use API search instead of local filtering
  searchFn = input<((query: string) => Observable<FormAutocompleteOption<T>[]>) | null>(null);

  // Outputs
  selectionChange = output<T | null>();

  // Internal state
  searchText = signal<string>("");
  filteredOptions = signal<FormAutocompleteOption<T>[]>([]);
  isSearching = signal<boolean>(false);
  private _value: T | null = null;
  private _selectedOption: FormAutocompleteOption<T> | null = null; // Store selected option
  private _disabled = false;
  private _lastOptionsLength = 0;
  private _filterTimeout: ReturnType<typeof setTimeout> | null = null; // Debounce timer
  private _searchSubscription: Subscription | null = null;

  // Callbacks for ControlValueAccessor
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange: (value: T | null) => void = (_value: T | null) => {
    // Placeholder for ControlValueAccessor
  };
  private onTouched: () => void = () => {
    // Placeholder for ControlValueAccessor
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this._disabled = this.disabled();
    }

    // When options change (including first time), update the display
    if (changes.options) {
      const currentOptions = this.options();
      // Check if options array has changed significantly
      if (currentOptions.length !== this._lastOptionsLength) {
        this._lastOptionsLength = currentOptions.length;
        this.updateDisplayForCurrentValue();
      }
      this.updateFilteredOptions();
    }

    // When disabled changes, update internal state
    if (changes.disabled) {
      this._disabled = this.disabled();
    }
  }

  ngOnDestroy(): void {
    // Cleanup debounce timer
    if (this._filterTimeout) {
      clearTimeout(this._filterTimeout);
    }
    // Cleanup search subscription
    if (this._searchSubscription) {
      this._searchSubscription.unsubscribe();
    }
  }

  private updateDisplayForCurrentValue(): void {
    if (this._value !== null && this._value !== undefined) {
      // First try to use stored selected option
      if (this._selectedOption && this._selectedOption.value === this._value) {
        if (this.searchText() !== this._selectedOption.label) {
          this.searchText.set(this._selectedOption.label);
        }
        return;
      }

      // Fallback to finding in current options
      const option = this.options().find((opt) => opt.value === this._value);
      if (option && this.searchText() !== option.label) {
        this.searchText.set(option.label);
        this._selectedOption = option; // Cache it
      }
    }
  }

  private updateFilteredOptions(): void {
    const searchTextValue = this.searchText();
    const search = (searchTextValue || "").toString().toLowerCase().trim();
    const opts = this.options();

    if (!search) {
      // No search text - show all options (virtual scroll handles rendering)
      this.filteredOptions.set(opts);
      return;
    }

    // Filter based on search text
    const filtered = opts.filter((option) => option.label.toLowerCase().includes(search));
    this.filteredOptions.set(filtered);
  }

  // ControlValueAccessor implementation
  writeValue(value: T | null): void {
    this._value = value;
    if (value === null || value === undefined) {
      this.searchText.set("");
      this._selectedOption = null;
    } else {
      // First check if we have a cached selected option
      if (this._selectedOption && this._selectedOption.value === value) {
        this.searchText.set(this._selectedOption.displayLabel || this._selectedOption.label);
      } else {
        // Try to find in current options
        const option = this.options().find((opt) => opt.value === value);
        if (option) {
          this.searchText.set(option.displayLabel || option.label);
          this._selectedOption = option; // Cache it
        }
      }
    }
    this.updateFilteredOptions();
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  onSearchChange(): void {
    // Clear any existing timeout
    if (this._filterTimeout) {
      clearTimeout(this._filterTimeout);
      this._filterTimeout = null;
    }

    // Cancel any ongoing search
    if (this._searchSubscription) {
      this._searchSubscription.unsubscribe();
      this._searchSubscription = null;
    }

    // Debounce search for better performance
    this._filterTimeout = setTimeout(() => {
      const searchFn = this.searchFn();
      if (searchFn) {
        // API search mode
        const query = this.searchText().trim();
        if (query.length >= 2) {
          // Require at least 2 characters for API search
          this.isSearching.set(true);
          this._searchSubscription = searchFn(query).subscribe({
            next: (results) => {
              this.filteredOptions.set(results);
              this.isSearching.set(false);
            },
            error: () => {
              this.filteredOptions.set([]);
              this.isSearching.set(false);
            },
          });
        } else {
          // If less than 2 characters, show empty or initial options
          this.filteredOptions.set(this.options());
        }
      } else {
        // Local search mode
        this.updateFilteredOptions();
      }
      this._filterTimeout = null;
    }, 300); // 300ms debounce for API search, 200ms for local
  }

  onFocus(): void {
    // Clear search text when field gets focus to allow fresh searching
    // but keep the underlying value intact
    if (this.hasSelection) {
      this.searchText.set("");
      this.updateFilteredOptions();
    }
    this.onTouched();
  }

  onClick(): void {
    // Clear search text when clicking on the field to allow fresh searching
    // but keep the underlying value intact
    if (this.hasSelection && this.searchText()) {
      this.searchText.set("");
      this.updateFilteredOptions();
    }
  }

  onPanelOpen(): void {
    this.updateFilteredOptions();
  }

  onPanelClose(): void {
    if (this._value !== null && this._value !== undefined) {
      // First try to use stored selected option
      if (this._selectedOption && this._selectedOption.value === this._value) {
        this.searchText.set(this._selectedOption.displayLabel || this._selectedOption.label);
      } else {
        // Fallback to finding in current options
        const option = this.options().find((opt) => opt.value === this._value);
        if (option) {
          this.searchText.set(option.displayLabel || option.label);
          this._selectedOption = option; // Cache it
        } else if (this._selectedOption) {
          // If not found in current options but we have cached option, use it
          this.searchText.set(this._selectedOption.displayLabel || this._selectedOption.label);
        }
      }
    } else {
      // If no value selected and search text doesn't match anything, clear it
      this.searchText.set("");
    }
    this.updateFilteredOptions();
  }

  onSelectionChange(value: T | null): void {
    this._value = value;

    if (value === null || value === undefined) {
      this.searchText.set("");
      this._selectedOption = null;
    } else {
      const option = this.filteredOptions().find((opt) => opt.value === value);
      if (option) {
        this.searchText.set(option.displayLabel || option.label);
        this._selectedOption = option; // Cache the selected option
      } else {
        // If not found in filtered options, check all options
        const allOption = this.options().find((opt) => opt.value === value);
        if (allOption) {
          this.searchText.set(allOption.displayLabel || allOption.label);
          this._selectedOption = allOption;
        }
      }
    }

    this.updateFilteredOptions();
    this.onChange(value);
    this.onTouched();
    this.selectionChange.emit(value);
  }

  clearSelection(): void {
    this.searchText.set("");
    this._value = null;
    this._selectedOption = null;
    this.updateFilteredOptions();
    this.onChange(null);
    this.onTouched();
    this.selectionChange.emit(null);
  }

  displayFn = (value: T | null): string => {
    if (value === null || value === undefined) {
      return "";
    }

    // First try cached selected option
    if (this._selectedOption && this._selectedOption.value === value) {
      return this._selectedOption.displayLabel || this._selectedOption.label;
    }

    // Look up the option using the value parameter (what Material passes)
    const option = this.options().find((opt) => opt.value === value);
    if (option) {
      this._selectedOption = option; // Cache it for future use
      return option.displayLabel || option.label;
    }

    // If option not found, return the value as string (fallback)
    return String(value);
  };

  get isDisabled(): boolean {
    return this._disabled;
  }

  get hasSelection(): boolean {
    return this._value !== null && this._value !== undefined;
  }

  // Track by function for virtual scroll performance
  trackByValue = (_index: number, option: FormAutocompleteOption<T>): T => {
    return option.value;
  };

  // Expose Math to template
  Math = Math;
}
